import type { InputHTMLAttributes } from 'react'
import { forwardRef, useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import InputWrap from './InputWrap'
import Input from './Input'
import styled from 'styled-components'
import { rem } from 'polished'
import { InputTextBase } from 'LuxKit/Typography/InputText'
import { DMY_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { EMAIL_PATTERN } from 'constants/regularExpressions'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'

const StyledInput = styled(Input)`
  ${InputTextBase}
  line-height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});
  width: 100%;
  border: none;
  background: transparent;
  transition: color 0.2s;
  padding: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.S};
  color: ${props => props.theme.palette.neutral.default.one};

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    color: ${props => props.theme.palette.neutral.default.four};
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${props => props.theme.palette.neutral.default.two};
    }
  }

  /*
    Browser things like auto fill and validation pop ups use the
    actual input element as their target. We want this to look like
    the whole element - so we need our input to take up the whole container

    When there's icon, reserve space for them and then they will be
    absolutely positioned.

    Icons for inputs are always 24px sized
  */
  &.icon-left {
    padding-left: ${rem(48)};
  }

  &.icon-right {
    padding-right: ${rem(48)};
  }
`

type CustomInputStateValidity = {
  emailDomainMismatch: boolean
}

type CustomValidatedInput = Omit<HTMLInputElement, 'validity'> & {
  validity: HTMLInputElement['validity'] & Partial<CustomInputStateValidity>
}

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  inputClassName?: string
  containerRef?: React.Ref<HTMLDivElement>
  /**
   * Override the 'required' error message
   */
  requiredErrorMessage?: string
  /**
   * Override any other validation error
   *
   * _For more granular error handling, use getInvalidMessage_
   */
  invalidErrorMessage?: string
  manualError?: boolean
  noValidationSpacing?: boolean
  noValidationMessage?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  /**
   * Content to render inside the input wrapper before the input element.
   */
  startContent?: React.ReactNode
  uppercase?: boolean
  helpText?: React.ReactNode
  getInvalidMessage?: (input: HTMLInputElement) => string | undefined
  onInputValidate?: (input: HTMLInputElement) => void
}

const TextInputOnly = forwardRef<HTMLInputElement | null, Props>((props, ref) => {
  const {
    value,
    defaultValue,
    className,
    children,
    inputClassName,
    disabled,
    type = 'text',
    pattern,
    style,
    startIcon,
    endIcon,
    startContent,
    minLength,
    maxLength,
    min,
    max,
    noValidationSpacing,
    noValidationMessage,
    onInputValidate,
    manualError,
    containerRef,
    getInvalidMessage,
    requiredErrorMessage,
    invalidErrorMessage,
    helpText,
    ...inputProps
  } = props

  const intl = useIntl()
  const [isDirty, setDirty] = useState<boolean>(!!(defaultValue || value))
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const handleDirtyChange = useCallback(() => {
    setDirty((currDirty) => {
      if (!currDirty) {
        return true
      } else {
        return currDirty
      }
    })
  }, [])

  const updateErrorMessage = useCallback((input: CustomValidatedInput) => {
    const validity = onInputValidate ? onInputValidate(input) : input.validity
    if (validity && !validity.valid) {
      let message = getInvalidMessage?.(input)
      if (!message) {
        if (validity.valueMissing && requiredErrorMessage) {
          message = requiredErrorMessage
        } else if (invalidErrorMessage) {
          message = invalidErrorMessage
        } else if (type === 'email' && (validity.patternMismatch || validity.emailDomainMismatch)) {
          message = intl.formatMessage({
            id: '6w4BOzCSZx3ZtE1fbUm1',
            defaultMessage: 'Please enter a valid email',
            description: 'Validation error shown when an invalid email address is entered',
          })
        } else if (validity.patternMismatch && type === 'tel') {
          message = intl.formatMessage({
            id: 'iwLS5TsLIEKq47bymM8R',
            defaultMessage: 'Phone number can contain numbers only',
            description: 'Validation error when phone number contains non-numeric characters',
          })
        } else if (validity.tooShort) {
          message = intl.formatMessage(
            {
              id: 'YUw70Kl9_sOuOvpIcTuV',
              defaultMessage: 'Please enter at least {minLength} characters',
              description: 'Validation error when input is shorter than minimum length',
            },
            { minLength },
          )
        } else if (validity.tooLong) {
          message = intl.formatMessage(
            {
              id: 'vc5o9wFP9VPemHfNYZ8a',
              defaultMessage: 'Please enter at most {maxLength} characters',
              description: 'Validation error when input exceeds maximum length',
            },
            { maxLength },
          )
        } else if (type === 'date' && validity.rangeUnderflow) {
          message = intl.formatMessage(
            {
              id: 'XfTQQUEXgcjKbcIp3Dfj',
              defaultMessage: 'Must be after {date}',
              description: 'Validation error when date is before the minimum allowed date',
            },
            { date: moment(min).format(DMY_DATE_FORMAT) },
          )
        } else if (type === 'date' && validity.rangeOverflow) {
          message = intl.formatMessage(
            {
              id: 'RxvuPdQoKE6kx9FbN5XI',
              defaultMessage: 'Must be before {date}',
              description: 'Validation error when date is after the maximum allowed date',
            },
            { date: moment(max).format(DMY_DATE_FORMAT) },
          )
        } else if (type === 'date' && validity.badInput) {
          message = intl.formatMessage({
            id: '1OPQyap3hQBunZRsZa6G',
            defaultMessage: 'Invalid date',
            description: 'Validation error shown when a date input value is malformed',
          })
        } else {
          message = input.validationMessage
        }
      }
      setErrorMessage(message)
    } else if (errorMessage) {
      setErrorMessage(undefined)
    }
  }, [errorMessage, getInvalidMessage, intl, invalidErrorMessage, max, maxLength, min, minLength, onInputValidate, requiredErrorMessage, type])

  const additionalValidators = useMemo(() => {
    const validators: Array<
      (input: HTMLInputElement) => { message: string, validityState: Partial<CustomInputStateValidity> } | undefined
    > = []

    /**
     * Having issues with users being unable to login due to these checks
     * Temporarily turning them off for now
     *
     */
    // if (!pattern && type === 'email') {
    // Checks for obvious typos in the email
    // E.g.: @gmail.con instead of @gmail.com
    // validators.push((input) => {
    //   if (!input.validity.patternMismatch && input.value) {
    //     const { isLikelyTypo } = checkEmailTypo(input.value)

    //     if (isLikelyTypo) {
    //       return {
    //         message: 'Please check the spelling of the email address',
    //         validityState: {
    //           emailDomainMismatch: true,
    //         },
    //       }
    //     }
    //   }
    // })
    // }

    return validators
  }, [])

  const inferredValidators = useCallback((input: HTMLInputElement) => {
    if (additionalValidators.length) {
      let validityMessage = ''
      let validityState: Partial<CustomInputStateValidity> = {}

      for (const validator of additionalValidators) {
        const validationResult = validator(input)

        // Assumes the first validator with result, meaning invalid
        if (validationResult) {
          validityMessage = validationResult.message
          validityState = validationResult.validityState
          break
        }
      }

      input.setCustomValidity(validityMessage)
      updateErrorMessage({ ...input, validity: { ...input.validity, ...validityState } })
    } else {
      updateErrorMessage(input)
    }
  }, [additionalValidators, updateErrorMessage])

  let displayableErrorMessage = isDirty ? errorMessage : undefined

  if (manualError) {
    displayableErrorMessage = invalidErrorMessage
  }

  const inferredPattern = pattern ?? (type === 'email' ? EMAIL_PATTERN : undefined)

  return <InputWrap
    ref={containerRef}
    className={className}
    error={displayableErrorMessage}
    disabled={disabled}
    noValidationSpacing={noValidationSpacing}
    noValidationMessage={noValidationMessage}
    startIcon={startIcon}
    endIcon={endIcon}
    helpText={helpText}
  >
    {startContent}
    <StyledInput
      {...inputProps}
      ref={ref}
      value={value}
      defaultValue={defaultValue}
      type={type}
      pattern={inferredPattern}
      min={min}
      minLength={minLength}
      max={max}
      maxLength={maxLength}
      disabled={disabled}
      className={clsx(inputClassName,
        { 'icon-left': !!startIcon, 'icon-right': !!endIcon },
      )}
      onErrorUpdate={inferredValidators}
      onDirtyChange={handleDirtyChange}
    />
    {children}
  </InputWrap>
})

export default TextInputOnly
